import {
  ChartResult,
  ChartResultCell,
  getSeriesColors,
} from '@employ-engage/data';

/**
 * Turns the engine's cross-tab into the row shape Recharts wants.
 *
 * The one rule that matters: a suppressed cell becomes null, never zero.
 * Recharts renders null as a gap, which is the truth — "we are not showing
 * you this" — whereas zero would read as "nobody said this", turning a privacy
 * control into a false data point.
 */

export type RechartsRow = Record<string, string | number | null>;

export type ChartSeries = {
  /** Key into each row. */
  key: string;
  label: string;
  color: string;
};

export type RechartsData = {
  rows: RechartsRow[];
  series: ChartSeries[];
  /** Key holding the axis label on each row. */
  categoryKey: string;
  /** True when any cell was withheld. */
  hasSuppressed: boolean;
  /** True when any cell carries an uncertainty range worth drawing. */
  hasIntervals: boolean;
  /**
   * True when a sample was too small for a percentage to mean anything, so
   * the caller should show counts instead of drawing a chart of them.
   */
  countOnly: boolean;
};

/** Recharts reads an error bar off the row, as [belowValue, aboveValue]. */
export const ERROR_KEY = '__error__';

export const CATEGORY_KEY = '__category__';

const cellValue = (cell: ChartResultCell | undefined): number | null => {
  if (!cell || cell.suppressed) return null;
  return cell.value;
};

/**
 * @param result the engine's output
 * @param mode which palette to colour with
 */
export const toRecharts = (
  result: ChartResult | null | undefined,
  mode: 'light' | 'dark' = 'light'
): RechartsData => {
  if (!result?.rowLabels?.length) {
    return {
      rows: [],
      series: [],
      categoryKey: CATEGORY_KEY,
      hasSuppressed: false,
      hasIntervals: false,
      countOnly: false,
    };
  }

  const rowLabels = result.rowLabels.map((l) => l[0] ?? '');
  const colLabels = result.colLabels.map((l) => l[0] ?? '');

  // A chart with no breakdown still needs one series to draw.
  const seriesLabels = colLabels.length ? colLabels : ['Responses'];
  const colors = getSeriesColors(seriesLabels, mode);
  const series = seriesLabels.map((label, i) => ({
    key: `s${i}`,
    label,
    color: colors[i],
  }));

  // Looked up by key rather than by position, so this does not depend on the
  // order the engine happened to emit cells in.
  const byPosition = new Map<string, ChartResultCell>();
  result.cells.forEach((cell) => {
    byPosition.set(`${cell.rowKey[0]}\u0000${cell.colKey[0]}`, cell);
  });

  let hasIntervals = false;
  const rows: RechartsRow[] = rowLabels.map((label, rowIndex) => {
    const row: RechartsRow = { [CATEGORY_KEY]: label };
    series.forEach((s, colIndex) => {
      const rowKey = result.rowKeys[rowIndex];
      const colKey = result.colKeys[colIndex] ?? result.colKeys[0];
      const cell = byPosition.get(`${rowKey}\u0000${colKey}`);
      row[s.key] = cellValue(cell);

      // Only drawn on a single-series chart: overlapping error bars across
      // stacked series are unreadable and imply a comparison that the stack
      // is not making.
      if (cell?.interval && !cell.suppressed && seriesLabels.length === 1) {
        hasIntervals = true;
        row[ERROR_KEY] = [
          Math.max(0, (cell.value ?? 0) - cell.interval.low),
          Math.max(0, cell.interval.high - (cell.value ?? 0)),
        ] as never;
      }
    });
    return row;
  });

  return {
    rows,
    series,
    categoryKey: CATEGORY_KEY,
    hasSuppressed: result.cells.some((c) => c.suppressed),
    hasIntervals,
    countOnly: result.cells.some((c) => c.treatment === 'count-only'),
  };
};
